import { consentPolicyVersion } from './consent';
import { categoryDictionaryVersion, cityDictionaryVersion } from './dictionaries';
import type { ApplyFormPayload } from './schema';

/**
 * Wire payload for `POST /api/public/merchant-applications`, mapping form values
 * to the field names from design.md "Approved application fields".
 *
 * Verified against the backend on 2026-09-04: every field below maps onto a
 * `MerchantApplicationRequest` component under Jackson SNAKE_CASE, and the
 * dictionary/consent versions are persisted rather than dropped. The public
 * OpenAPI contract is still unrecorded (PLI-78), so the agreement holds by
 * verification, not by obligation — a one-sided rename breaks the integration
 * silently.
 *
 * `phone` is sent from the form and stays that way: intake from the landing is
 * anonymous by decision (2026-09-03), so no verified lead identity supplies it.
 */
export interface MerchantApplicationRequest {
  contact_person: string;
  business_name: string;
  phone: string;
  email: string;
  business_type: string;
  bin: string;
  business_category_id: string;
  business_category_dictionary_version: string;
  point_count: number;
  city_ids: Array<string>;
  city_dictionary_version: string;
  personal_data_consent: true;
  consent_version: string;
  business_description?: string;
}

export function buildApplicationRequest(values: ApplyFormPayload): MerchantApplicationRequest {
  const request: MerchantApplicationRequest = {
    contact_person: values.contactName,
    business_name: values.businessName,
    phone: values.phone,
    email: values.email,
    business_type: values.businessType,
    bin: values.bin,
    business_category_id: values.category,
    business_category_dictionary_version: categoryDictionaryVersion,
    point_count: values.locationsCount,
    city_ids: [values.cities],
    city_dictionary_version: cityDictionaryVersion,
    personal_data_consent: true,
    consent_version: consentPolicyVersion,
  };

  const description = values.description?.trim();
  if (description) {
    request.business_description = description;
  }

  return request;
}
